/**
 * Landing 機能の hooks。
 * - useLandings: 自分の編集可能な landings 一覧
 * - useLanding: 単一 landing
 * - useLandingSection: 単一 section（編集 Sheet 開時に lazy 取得）
 * - useUpdateLanding: メタ更新
 * - useUpdateLandingSection: section content 更新（blur 時保存）
 * - useGenerateLanding / useRegenerate* / usePublishLanding: callable function 呼び出し
 */
import type {
  Landing,
  LandingIndustryRecipe,
  LandingReferenceDocument,
  LandingSectionDoc,
  LandingSectionRef,
  LandingVisualIdentity,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions as fns } from '@/lib/firebase';
import { safeAddDoc, safeUpdateDoc } from '@/lib/safeWrite';

export interface LandingRecord extends Landing {
  id: string;
}

export function useLandings() {
  return useQuery({
    queryKey: ['landings'],
    queryFn: async (): Promise<LandingRecord[]> => {
      const snap = await getDocs(query(collection(db, 'landings'), orderBy('updatedAt', 'desc')));
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Landing) }));
    },
  });
}

export function useLanding(id: string | null | undefined) {
  return useQuery({
    queryKey: ['landings', id],
    enabled: !!id,
    queryFn: async (): Promise<LandingRecord | null> => {
      if (!id) return null;
      const snap = await getDoc(doc(db, 'landings', id));
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as Landing) };
    },
  });
}

export interface LandingSectionRecord extends LandingSectionDoc {
  id: string;
}

export function useLandingSection(
  landingId: string | null | undefined,
  sectionId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['landings', landingId, 'sections', sectionId],
    enabled: !!landingId && !!sectionId,
    queryFn: async (): Promise<LandingSectionRecord | null> => {
      if (!landingId || !sectionId) return null;
      const snap = await getDoc(doc(db, 'landings', landingId, 'sections', sectionId));
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as LandingSectionDoc) };
    },
  });
}

interface UpdateLandingArgs {
  id: string;
  data: Partial<Landing>;
}

export function useUpdateLanding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: UpdateLandingArgs) => {
      await safeUpdateDoc(doc(db, 'landings', id), {
        ...data,
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['landings'] });
      qc.invalidateQueries({ queryKey: ['landings', id] });
    },
  });
}

interface UpdateSectionArgs {
  landingId: string;
  sectionId: string;
  patch: Partial<LandingSectionDoc> & {
    contentPatch?: Record<string, unknown>;
    stylePatch?: Record<string, unknown>;
  };
}

export function useUpdateLandingSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ landingId, sectionId, patch }: UpdateSectionArgs) => {
      const { contentPatch, stylePatch, ...rest } = patch;
      const payload: Record<string, unknown> = {
        ...rest,
        updatedAt: serverTimestamp(),
      };
      if (contentPatch) {
        for (const [k, v] of Object.entries(contentPatch)) {
          payload[`content.${k}`] = v;
        }
      }
      if (stylePatch) {
        for (const [k, v] of Object.entries(stylePatch)) {
          payload[`style.${k}`] = v;
        }
      }
      await safeUpdateDoc(doc(db, 'landings', landingId, 'sections', sectionId), payload);
    },
    onSuccess: (_, { landingId, sectionId }) => {
      qc.invalidateQueries({
        queryKey: ['landings', landingId, 'sections', sectionId],
      });
      qc.invalidateQueries({ queryKey: ['landings', landingId] });
      // PreviewCanvas が使う「全セクションまとめ取得」キャッシュも無効化しないと
      // プレビューの内容が古いまま残る（公開ページと食い違う原因）。
      qc.invalidateQueries({
        queryKey: ['landings', landingId, 'sections', 'all'],
      });
    },
  });
}

interface GenerateLandingArgs {
  /** 既存 landing を上書きする場合に指定 */
  landingId?: string;
  sourceCampaignId: string;
  promptInsertion?: Record<string, boolean>;
  brand: string;
  industry: LandingIndustryRecipe;
  /** 'recipe' (default) or 'ai_design' */
  generationMode?: 'recipe' | 'ai_design';
  visualIdentity: LandingVisualIdentity;
  domain?: string;
  title?: string;
  slug?: string;
  /** 添付した参考資料（ブラウザで取り出した本文）。保存はされない */
  referenceDocuments?: LandingReferenceDocument[];
}

export function useGenerateLanding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: GenerateLandingArgs) => {
      // generateLanding は Claude を呼ぶため最大 540 秒かかる可能性。
      // httpsCallable のデフォルト 70 秒では切れるので明示的に延長。
      const fn = httpsCallable(fns, 'generateLanding', { timeout: 540_000 });
      const res = await fn(args);
      return res.data as { landingId: string; slug: string };
    },
    onSuccess: (_, { landingId }) => {
      qc.invalidateQueries({ queryKey: ['landings'] });
      if (landingId) {
        qc.invalidateQueries({ queryKey: ['landings', landingId] });
        qc.invalidateQueries({ queryKey: ['landings', landingId, 'sections', 'all'] });
      }
    },
  });
}

/**
 * 空の Landing を新規作成する（キャンペーン未選択 / セクションなしの初期状態）。
 * 「新規作成」ボタンクリックで即エディタ画面に遷移するために使用。
 */
export function useCreateEmptyLanding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { ownerId: string }): Promise<string> => {
      const ref = await safeAddDoc(collection(db, 'landings'), {
        ownerId: args.ownerId,
        title: '無題のランディング',
        slug: '',
        status: 'draft',
        sourceCampaignId: '',
        promptInsertion: {},
        brand: '',
        industry: 'saas_b2b',
        visualIdentity: 'light',
        domain: '',
        brandSettings: {
          logoStoragePath: null,
          primaryColor: null,
          accentColor: null,
          headingFont: null,
          bodyFont: null,
          socialLinks: {},
        },
        sections: [],
        assignedCampaigns: { formIds: [], mailRecipientIds: [], lineNewsIds: [] },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        publishedAt: null,
      });
      return ref.id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['landings'] }),
  });
}

export function useRegenerateLandingSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      landingId: string;
      sectionId: string;
      instruction?: string;
      /** false にすると、現在の内容を踏襲せずブランド情報からゼロベース生成 (default: true) */
      useCurrentContent?: boolean;
    }) => {
      const fn = httpsCallable(fns, 'regenerateLandingSection', { timeout: 180_000 });
      const res = await fn(args);
      return res.data as { content: Record<string, unknown> };
    },
    onSuccess: (_, { landingId, sectionId }) => {
      qc.invalidateQueries({ queryKey: ['landings', landingId, 'sections', sectionId] });
      qc.invalidateQueries({ queryKey: ['landings', landingId] });
      qc.invalidateQueries({ queryKey: ['landings', landingId, 'sections', 'all'] });
    },
  });
}

export function useRegenerateLandingAllSections() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      landingId: string;
      referenceDocuments?: LandingReferenceDocument[];
    }) => {
      const fn = httpsCallable(fns, 'regenerateLandingAllSections', {
        timeout: 540_000,
      });
      const res = await fn(args);
      return res.data as { updatedSectionIds: string[] };
    },
    onSuccess: (_, { landingId }) => {
      qc.invalidateQueries({ queryKey: ['landings', landingId] });
      qc.invalidateQueries({ queryKey: ['landings', landingId, 'sections', 'all'] });
    },
  });
}

/**
 * ランディングを複製する。
 *  - mode='simple': content / brandSettings / visualIdentity すべてそのまま deep copy
 *  - mode='ai':     セクション構成は踏襲、各セクションの content を AI で再生成
 *                   + 任意で薬事法 NG ワード置換
 */
export function useDuplicateLanding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      sourceLandingId: string;
      newTitle?: string;
      mode: 'simple' | 'ai';
      /** 01 全体の方向性: 全セクションに反映する指示 */
      overallDirection?: string;
      /** 02 キーポイント: 最適セクションのみに反映 / 新規セクション化する情報 */
      keyPoint?: string;
      /** @deprecated overallDirection を使用 */
      aiInstruction?: string;
      sanitizePharmaceutical?: boolean;
    }) => {
      const fn = httpsCallable(fns, 'duplicateLanding', { timeout: 540_000 });
      const res = await fn(args);
      return res.data as {
        landingId: string;
        slug: string;
        keyPoint?: {
          provided: boolean;
          applied: boolean;
          action: 'update' | 'create' | null;
          targets?: string[];
          createdSection?: { type: string; title: string };
        };
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['landings'] });
    },
  });
}

export function useRegenerateLandingField() {
  return useMutation({
    mutationFn: async (args: {
      landingId: string;
      sectionId: string;
      fieldPath: string;
      instruction?: string;
    }) => {
      const fn = httpsCallable(fns, 'regenerateLandingField');
      const res = await fn(args);
      return res.data as { value: unknown };
    },
  });
}

export function usePublishLanding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { landingId: string; slug?: string }) => {
      const fn = httpsCallable(fns, 'publishLanding');
      const res = await fn(args);
      return res.data as { slug: string; publicUrl: string };
    },
    onSuccess: (_, { landingId }) => {
      qc.invalidateQueries({ queryKey: ['landings'] });
      qc.invalidateQueries({ queryKey: ['landings', landingId] });
    },
  });
}

/**
 * Landing 本体 + sections / history サブコレクションを cascade 削除。
 * history は Cloud Function のみ書込可なので必ずこの hook 経由で削除する。
 */
export function useDeleteLanding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { landingId: string }) => {
      const fn = httpsCallable(fns, 'deleteLanding', { timeout: 120_000 });
      const res = await fn(args);
      return res.data as { id: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['landings'] });
    },
  });
}

export function useUnpublishLanding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { landingId: string }) => {
      const fn = httpsCallable(fns, 'unpublishLanding');
      const res = await fn(args);
      return res.data;
    },
    onSuccess: (_, { landingId }) => {
      qc.invalidateQueries({ queryKey: ['landings'] });
      qc.invalidateQueries({ queryKey: ['landings', landingId] });
    },
  });
}

/**
 * 公開ランディングを slug で取得（未認証アクセス可）
 */
export function usePublicLanding(slug: string | null | undefined) {
  return useQuery({
    queryKey: ['public_landings', slug],
    enabled: !!slug,
    queryFn: async (): Promise<LandingRecord | null> => {
      if (!slug) return null;
      const snap = await getDocs(
        query(
          collection(db, 'landings'),
          where('slug', '==', slug),
          where('status', '==', 'published'),
        ),
      );
      const doc0 = snap.docs[0];
      if (!doc0) return null;
      return { id: doc0.id, ...(doc0.data() as Landing) };
    },
  });
}

/**
 * 公開ページ用: landing id から個別 Google タグだけを取得（未認証アクセス可）。
 * フォームの CTA に付く ?lp=<id> から当該 LP のタグを解決するために使う。
 * published な landing は id 単位で公開読取可（firestore.rules）。
 */
export function usePublicLandingGoogleTag(landingId: string | null | undefined) {
  return useQuery({
    queryKey: ['public_landing_gtag', landingId],
    enabled: !!landingId,
    queryFn: async (): Promise<{ headHtml: string; bodyHtml: string } | null> => {
      if (!landingId) return null;
      const snap = await getDoc(doc(db, 'landings', landingId));
      if (!snap.exists()) return null;
      const tag = (snap.data() as Landing).googleTag;
      return tag ? { headHtml: tag.headHtml ?? '', bodyHtml: tag.bodyHtml ?? '' } : null;
    },
  });
}

export async function loadLandingSections(
  landingId: string,
): Promise<Array<LandingSectionRecord & { ref: LandingSectionRef | undefined }>> {
  const snap = await getDocs(
    query(collection(db, 'landings', landingId, 'sections'), orderBy('order', 'asc')),
  );
  return snap.docs.map((d) => ({
    id: d.id,
    ref: undefined,
    ...(d.data() as LandingSectionDoc),
  }));
}
